/**
 * Trajectory transcript assembly for the review page.
 *
 * A trajectory = every trace in one conversation, sorted by gateway
 * timestamp. Chat-completions clients resend the whole message history on
 * every request, so trace N's request body contains trace N-1's messages
 * as a prefix. We delta-merge: for each trace, emit only the messages the
 * previous trace hadn't seen, then the trace's own response message. Every
 * event carries its source trace id so the flag pin can anchor to the
 * flagged trace.
 *
 * Unparseable bodies degrade to a visible `raw` event — we never silently
 * hide content.
 */

import { isRecord } from '@/lib/guards'

export interface ToolEvent {
  kind: 'tool'
  traceId: string
  name: string
  args: string
  /** Tool output; null until (unless) a matching tool message arrives. */
  result: string | null
  /**
   * Trace whose request carried `result`; null while `result` is. Usually
   * the trace AFTER the call's own: the client sends the output back in
   * its next request. The review transcript shows the result on the call's
   * row and points at this request, so a "request N" citation that means
   * the result (TRA-1080) can be followed back to it.
   */
  resultTraceId: string | null
  callId: string | null
}

export type TranscriptEvent =
  | { kind: 'user'; traceId: string; text: string; images: string[] }
  | { kind: 'assistant'; traceId: string; text: string }
  | { kind: 'thinking'; traceId: string; text: string }
  | { kind: 'system'; traceId: string; text: string }
  | ToolEvent
  | { kind: 'raw'; traceId: string; label: string; text: string }

export interface RawToolCall {
  id: string | null
  name: string
  args: string
}

export interface RawMessage {
  role: string
  content: string
  /** `image_url` part URLs (data: or https:), in message order. */
  images: string[]
  toolCalls: RawToolCall[]
  toolCallId: string | null
  reasoning: string | null
}

/** Flatten OpenAI content (string or typed part array) to plain text. */
function contentToText(content: unknown): string {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (isRecord(part) && typeof part.text === 'string') return part.text
        return ''
      })
      .join('')
  }
  return ''
}

/**
 * Image URLs from an OpenAI typed content array. Anything that is not an
 * `image_url` part with a string url is ignored here, because
 * contentToText already keeps the text parts.
 */
function contentImages(content: unknown): string[] {
  if (!Array.isArray(content)) return []
  const urls: string[] = []
  for (const part of content) {
    if (!isRecord(part) || part.type !== 'image_url') continue
    const ref = part.image_url
    if (typeof ref === 'string') urls.push(ref)
    else if (isRecord(ref) && typeof ref.url === 'string') urls.push(ref.url)
  }
  return urls
}

function parseToolCalls(value: unknown): RawToolCall[] {
  if (!Array.isArray(value)) return []
  return value.map((call): RawToolCall => {
    const record = isRecord(call) ? call : {}
    const fn = isRecord(record.function) ? record.function : {}
    return {
      id: typeof record.id === 'string' ? record.id : null,
      name: typeof fn.name === 'string' && fn.name !== '' ? fn.name : 'tool',
      args: typeof fn.arguments === 'string' ? fn.arguments : JSON.stringify(call),
    }
  })
}

/**
 * Reasoning field aliases, in precedence order.
 *
 * Upstreams disagree on the name and only ever send one, so these are
 * alternatives, not additive — matching `chat_proxy.py`'s
 * `reasoning_content or thinking`.
 */
const REASONING_KEYS = ['reasoning_content', 'reasoning', 'thinking'] as const

function pickReasoning(source: Record<string, unknown>): string | null {
  for (const key of REASONING_KEYS) {
    const value = source[key]
    if (typeof value === 'string' && value !== '') return value
  }
  return null
}

export function parseMessage(value: unknown): RawMessage | null {
  if (!isRecord(value) || typeof value.role !== 'string') return null
  const reasoning = pickReasoning(value)
  return {
    role: value.role,
    content: contentToText(value.content),
    images: contentImages(value.content),
    toolCalls: parseToolCalls(value.tool_calls),
    toolCallId: typeof value.tool_call_id === 'string' ? value.tool_call_id : null,
    reasoning,
  }
}

interface ParsedRequest {
  messages: RawMessage[]
  /** Length of the request's `tools` array — 0 when none was sent. */
  toolCount: number
}

/** Parse a chat-completions request body's `messages` array + tool schema count. */
function parseRequest(bodyText: string | null | undefined): ParsedRequest | null {
  if (!bodyText) return null
  try {
    const parsed: unknown = JSON.parse(bodyText)
    if (!isRecord(parsed) || !Array.isArray(parsed.messages)) return null
    const messages = parsed.messages.map(parseMessage)
    if (messages.some((m) => m === null)) return null
    return {
      messages: messages as RawMessage[],
      toolCount: Array.isArray(parsed.tools) ? parsed.tools.length : 0,
    }
  } catch {
    return null
  }
}

/**
 * Reassemble an SSE chat-completions stream into the one assistant message
 * it encodes.
 *
 * The gateway stores a streaming response verbatim — the literal
 * `data: {...}\n\ndata: [DONE]` text. Rendering that raw is what made
 * "called execute with args X" read as machine output. Deltas concatenate:
 * `content` and reasoning append as strings; `tool_calls` accumulate by
 * their `index` (name arrives once in the first frame, `arguments` stream
 * in fragments across many).
 *
 * A frame we can't parse returns null so the caller degrades to a visible
 * `raw` event — we never render a half-assembled message as if it were
 * the whole truth.
 */
/** Accumulator threaded through the frames of one SSE stream. */
interface SseAccumulator {
  content: string
  reasoning: string
  calls: Map<number, RawToolCall>
  /** True once a chat-completions-shaped frame has been seen. */
  sawChoices: boolean
}

/** Extract the `data:` payloads, dropping the stream's framing. */
export function ssePayloads(bodyText: string): string[] {
  return bodyText
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).trim())
    .filter((payload) => payload !== '' && payload !== '[DONE]')
}

/**
 * Merge one frame's `tool_calls` fragments into the accumulator.
 *
 * Returns false when a fragment carries no numeric `index`. That index is
 * the only thing tying fragments of the same call together, so guessing
 * would silently splice one call's arguments into another — better to fall
 * back to the raw body and let a human read it.
 */
function mergeToolCallDeltas(calls: Map<number, RawToolCall>, raw: unknown[]): boolean {
  for (const fragment of raw) {
    if (!isRecord(fragment)) continue
    if (typeof fragment.index !== 'number') return false
    const existing = calls.get(fragment.index) ?? { id: null, name: '', args: '' }
    const fn = isRecord(fragment.function) ? fragment.function : {}
    calls.set(fragment.index, {
      id: typeof fragment.id === 'string' ? fragment.id : existing.id,
      name: mergeToolName(
        existing.name,
        typeof fn.name === 'string' ? fn.name : '',
      ),
      args: existing.args + (typeof fn.arguments === 'string' ? fn.arguments : ''),
    })
  }
  return true
}

/**
 * Combine a streamed function-name fragment with what we have so far.
 *
 * Three provider shapes seen in the wild, and a naive rule breaks two of
 * them: OpenAI sends the name once, some split it across frames ("get"
 * then "Rate"), and some repeat it — either identically or cumulatively —
 * on every fragment. Plain assignment loses split names; plain
 * concatenation turns repeats into "getRategetRate", which then also
 * poisons the tool filter's list.
 */
function mergeToolName(existing: string, incoming: string): string {
  if (incoming === '') return existing
  if (existing === '') return incoming
  if (incoming === existing) return existing
  // Cumulative repeat: each frame carries the whole name so far.
  if (incoming.startsWith(existing)) return incoming
  return existing + incoming
}

/** Fold one parsed frame into the accumulator. False means "give up". */
function applySseFrame(acc: SseAccumulator, frame: unknown): boolean {
  if (!isRecord(frame)) return false
  // A mid-stream error envelope is real content the reader needs to see —
  // never quietly skip it and present the partial message as complete.
  // Presence is not enough: providers ship `"error": null` on ordinary
  // chunks, and treating that as a failure would dump a complete reply.
  if (frame.error !== null && frame.error !== undefined) return false
  // Frames without `choices` are keep-alives or provider-native envelopes
  // that carry nothing we render. Skipping beats discarding a fully
  // assembled message over one unrecognized frame.
  if (!Array.isArray(frame.choices)) return true
  acc.sawChoices = true

  const choice: unknown = frame.choices[0]
  if (!isRecord(choice)) return true
  const delta: unknown = choice.delta
  if (!isRecord(delta)) return true

  if (typeof delta.content === 'string') acc.content += delta.content
  // One alias per frame — appending every match would duplicate the
  // reasoning for any upstream that sends two spellings of it.
  const frameReasoning = pickReasoning(delta)
  if (frameReasoning !== null) acc.reasoning += frameReasoning

  if (!Array.isArray(delta.tool_calls)) return true
  return mergeToolCallDeltas(acc.calls, delta.tool_calls)
}

function parseSseResponse(bodyText: string): RawMessage | null {
  const acc: SseAccumulator = {
    content: '',
    reasoning: '',
    calls: new Map(),
    sawChoices: false,
  }

  for (const payload of ssePayloads(bodyText)) {
    let frame: unknown
    try {
      frame = JSON.parse(payload)
    } catch {
      return null
    }
    if (!applySseFrame(acc, frame)) return null
  }

  // Nothing chat-completions-shaped in the whole stream — we have no
  // message, so show the raw body rather than a convincing empty one.
  if (!acc.sawChoices) return null
  return {
    role: 'assistant',
    content: acc.content,
    images: [],
    toolCalls: [...acc.calls.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, call]) => ({ ...call, name: call.name === '' ? 'tool' : call.name })),
    toolCallId: null,
    reasoning: acc.reasoning === '' ? null : acc.reasoning,
  }
}

/**
 * Is this body an SSE stream? Detected from any `data:` line, not the
 * first one: a stream may open with an `event:` field or a heartbeat
 * comment.
 */
export function isSseBody(bodyText: string): boolean {
  return bodyText.split('\n').some((line) => line.startsWith('data:'))
}

/** Parse a chat-completions response body's assistant message. */
export function parseResponseMessage(bodyText: string | null | undefined): RawMessage | null {
  if (!bodyText) return null
  // Streaming and non-streaming are distinct wire formats, not a fallback
  // chain — branch on the shape rather than parse-and-hope.
  if (isSseBody(bodyText)) {
    return parseSseResponse(bodyText)
  }
  try {
    const parsed: unknown = JSON.parse(bodyText)
    if (!isRecord(parsed) || !Array.isArray(parsed.choices)) return null
    const first: unknown = parsed.choices[0]
    if (!isRecord(first)) return null
    return parseMessage(first.message)
  } catch {
    return null
  }
}

/**
 * Every tool call emitted so far, trajectory-wide.
 *
 * Scope matters: a turn's request carries the `tool` result for a call the
 * PREVIOUS turn's response made, so matching can't be scoped to one turn's
 * event array. Entries are the same objects held by the turn sections, so
 * setting `result` here fills in the rendered event.
 */
type ToolRegistry = ToolEvent[]

/**
 * Attach a tool-output message to its call: by tool_call_id when present,
 * else positionally. An id with NO match falls back to position too —
 * clients reserialize history with fresh call ids (same name and args),
 * and prefix alignment deliberately ignores ids for exactly that reason,
 * so a strict id match here would orphan the result as raw output while
 * its call showed "no output captured".
 *
 * Positional attachment is scoped to the MOST RECENT turn's open calls: a
 * call left permanently unanswered by an earlier turn must never absorb a
 * later, unrelated result (which would also leave the real producer
 * showing no output). In-order pairing within the latest turn's parallel
 * calls is unchanged. Returns false when no open call exists (caller
 * emits a raw event instead — visible, never dropped).
 */
function attachToolResult(
  registry: ToolRegistry,
  message: RawMessage,
  traceId: string,
): boolean {
  const openCalls = registry.filter((e) => e.result === null)
  const latestTrace = openCalls[openCalls.length - 1]?.traceId
  const recentOpen = openCalls.filter((e) => e.traceId === latestTrace)
  const target =
    message.toolCallId !== null
      ? (openCalls.find((e) => e.callId === message.toolCallId) ?? recentOpen[0])
      : recentOpen[0]
  if (!target) return false
  target.result = message.content
  target.resultTraceId = traceId
  return true
}

function emitMessage(
  events: TranscriptEvent[],
  message: RawMessage,
  traceId: string,
  registry: ToolRegistry,
): void {
  if (message.role === 'tool') {
    if (!attachToolResult(registry, message, traceId)) {
      events.push({ kind: 'raw', traceId, label: 'tool output', text: message.content })
    }
    return
  }
  if (message.role === 'system') {
    events.push({ kind: 'system', traceId, text: message.content })
    return
  }
  if (message.role === 'user') {
    events.push({ kind: 'user', traceId, text: message.content, images: message.images })
    return
  }
  // assistant (and anything unexpected renders through the same path so it
  // stays visible): thinking first, then tool calls, then the visible text.
  if (message.reasoning !== null) {
    events.push({ kind: 'thinking', traceId, text: message.reasoning })
  }
  for (const call of message.toolCalls) {
    const event: ToolEvent = {
      kind: 'tool',
      traceId,
      name: call.name,
      args: call.args,
      result: null,
      resultTraceId: null,
      callId: call.id,
    }
    events.push(event)
    registry.push(event)
  }
  if (message.content !== '') {
    events.push({ kind: 'assistant', traceId, text: message.content })
  }
}

export interface TrajectoryTurn {
  traceId: string
  /**
   * Full R2 key of the trace, read off the object the server fetched.
   * Null when the reader could not supply one; such a request cannot be
   * opened for review on its own (TRA-1088).
   */
  traceKey: string | null
  requestBody: string | null
  responseBody: string | null
  /** Gateway-measured round-trip, milliseconds. */
  latencyMs: number
  promptTokens: number | null
  completionTokens: number | null
  /** Model the client asked for (not the upstream-mapped name). */
  model: string | null
  /** Gateway-recorded HTTP status of the upstream response. */
  statusCode: number
  /**
   * Delta storage (gateway PR #36): when present, `requestBody.messages`
   * holds only `messages[fromIndex:]` of what the client actually sent.
   * Null on full-body docs (no conversation, non-chat, pre-delta docs).
   */
  requestDelta: { fromIndex: number; fullMessageCount: number } | null
  /**
   * Gateway-detected thread edit: the client re-sent fewer messages than
   * the stored watermark, so this turn's body is the FULL rewritten thread.
   */
  historyEdited: boolean
  timestamp: string
}

/**
 * One request in the trajectory, carrying only the messages it ADDED.
 *
 * `events` is the delta — what this turn contributes that no prior turn
 * showed. Concatenating every turn's delta reproduces the complete
 * conversation exactly once, which is what the transcript renders; the
 * per-turn numbers feed the turns metrics table.
 */
export interface TranscriptTurn {
  traceId: string
  /** 1-based position in the trajectory. */
  index: number
  events: TranscriptEvent[]
  /** Total messages the client re-sent this turn (prefix + new). */
  messageCount: number
  /**
   * Messages this turn actually added — NOT `events.length`. One assistant
   * message can fan out to thinking + tool + text events, and a tool-result
   * message produces no event at all (it mutates the earlier call's event),
   * so the two numbers diverge in both directions.
   */
  newMessageCount: number
  /** Tool schemas attached to the request — shifts here are worth seeing. */
  toolCount: number
  latencyMs: number
  promptTokens: number | null
  completionTokens: number | null
  model: string | null
  statusCode: number
  timestamp: string
}

/**
 * Delta-merge sorted trace bodies into per-turn sections.
 *
 * Chat-completions clients resend the entire history every turn, so turn N's
 * request contains turn N-1's messages as a prefix. Rendering that prefix is
 * what made eight progressing turns look byte-identical: all growth is
 * appended after the last `user` message, so a naive "show the trailing user
 * message" renderer shows index 3 forever.
 */
/** Mutable state carried across turns by the delta merge. */
interface MergeState {
  /** Tool calls awaiting results, trajectory-wide. */
  registry: ToolRegistry
  /** Messages already rendered, in order — the next request's expected prefix. */
  seen: RawMessage[]
}

/**
 * Does this request actually open with the history we've already rendered?
 *
 * Alignment is STRUCTURAL, not byte-exact, and the difference matters a
 * lot.
 *
 * Byte-exact matching sounds safer but regresses into the bug this whole
 * view exists to fix: a system prompt carrying a live timestamp (the
 * motivating Wilson run has exactly this) differs every single turn, so
 * every turn resets and re-renders the entire conversation — "every turn
 * looks the same", back again, and /review has no prefix toggle to
 * collapse it.
 *
 * So the boundary follows the ROLE sequence, which only changes when the
 * client genuinely restructures history (pruning or reordering messages).
 * Content that drifts in place is still counted and reported, so an edited
 * earlier message is announced rather than silently absorbed.
 */
interface Alignment {
  /** Messages of this request already rendered; the rest are new. */
  boundary: number
  /** Already-seen messages whose content changed since we rendered them. */
  drifted: number
}

function sameToolCalls(a: RawToolCall[], b: RawToolCall[]): boolean {
  if (a.length !== b.length) return false
  return a.every((call, i) => {
    const other = b[i]
    return other !== undefined && call.name === other.name && call.args === other.args
  })
}

/** Same message, on everything a reader would see? */
function sameContent(a: RawMessage, b: RawMessage): boolean {
  // Reasoning counts as changed only when both sides have it. Clients
  // routinely omit reasoning when re-sending an assistant turn, and
  // treating an absent field as an edit would flag drift on nearly every
  // tool-using conversation.
  const reasoningChanged =
    a.reasoning !== null && b.reasoning !== null && a.reasoning !== b.reasoning
  return (
    a.content === b.content &&
    a.images.join('\n') === b.images.join('\n') &&
    !reasoningChanged &&
    sameToolCalls(a.toolCalls, b.toolCalls)
  )
}

function alignPrefix(messages: RawMessage[], seen: RawMessage[]): Alignment {
  // Shorter than what we rendered, or the roles diverge: the client reset
  // or rewrote the shape of the conversation. Render all of it — a
  // restructure is exactly the case where a positional merge would hide
  // genuinely new messages.
  if (messages.length < seen.length) return { boundary: 0, drifted: 0 }
  let drifted = 0
  for (const [i, expected] of seen.entries()) {
    const actual = messages[i]
    if (actual === undefined || actual.role !== expected.role) {
      return { boundary: 0, drifted: 0 }
    }
    if (!sameContent(actual, expected)) drifted += 1
  }
  return { boundary: seen.length, drifted }
}

/** Split one request at the already-seen boundary; only the new messages render. */
function splitRequest(
  request: ParsedRequest,
  seen: RawMessage[],
): { fresh: RawMessage[]; drifted: number; reset: boolean } {
  const { boundary, drifted } = alignPrefix(request.messages, seen)
  return {
    fresh: request.messages.slice(boundary),
    drifted,
    // The client abandoned the history we rendered (shorter, or the role
    // sequence diverged) — the whole request re-renders from the top.
    reset: boundary === 0 && seen.length > 0,
  }
}

/**
 * Placeholders for messages the transcript holds a position for but never
 * rendered. The role is one no real message can carry, so a later
 * full-body turn role-mismatches against them and resets honestly.
 */
function missingMessages(count: number): RawMessage[] {
  return Array.from(
    { length: count },
    (): RawMessage => ({
      role: '(missing)',
      content: '',
      images: [],
      toolCalls: [],
      toolCallId: null,
      reasoning: null,
    }),
  )
}

function buildTurnSection(
  turn: TrajectoryTurn,
  index: number,
  state: MergeState,
): TranscriptTurn {
  const events: TranscriptEvent[] = []
  const request = parseRequest(turn.requestBody)
  let newMessageCount = 0
  let messageCount = request?.messages.length ?? 0

  if (request === null) {
    events.push({
      kind: 'raw',
      traceId: turn.traceId,
      label: 'request (unrecognized shape)',
      text: turn.requestBody ?? '(no body captured)',
    })
  } else if (turn.requestDelta !== null) {
    // Delta-stored doc: the body is already the new tail — no prefix
    // alignment needed. `fromIndex` is the tail's absolute position in the
    // client's message array, which `state.seen` tracks, so overlapping
    // tails (concurrent turns raced the gateway's watermark) dedupe by
    // absolute index instead of re-rendering.
    const { fromIndex, fullMessageCount } = turn.requestDelta
    messageCount = fullMessageCount
    if (fromIndex > state.seen.length) {
      // A gap: an intermediate turn's doc is missing (its trace failed to
      // load — /review renders what resolved). Splicing the tail directly
      // onto the shorter `seen` would shift every later turn's absolute
      // index. Announce it, and pad with a role no real message can carry
      // so a later full-body turn role-mismatches and resets honestly.
      const gap = fromIndex - state.seen.length
      events.push({
        kind: 'raw',
        traceId: turn.traceId,
        label: 'gap in trajectory',
        text:
          `${gap.toString()} earlier message${gap === 1 ? '' : 's'} of this ` +
          'conversation did not load and are missing above.',
      })
      state.seen = [...state.seen, ...missingMessages(gap)]
    }
    const overlap = Math.min(
      Math.max(0, state.seen.length - fromIndex),
      request.messages.length,
    )
    const fresh = request.messages.slice(overlap)
    newMessageCount += fresh.length
    for (const message of fresh) {
      emitMessage(events, message, turn.traceId, state.registry)
    }
    state.seen = [...state.seen.slice(0, fromIndex), ...request.messages]
  } else {
    if (turn.historyEdited) {
      // Gateway-authoritative: the client rewrote its history, so this doc
      // carries the full rewritten thread. Reset the merge state HERE, not
      // via alignment — a rewrite that keeps the role sequence (a message
      // replaced in place) would still align, and the transcript would
      // announce a restart while quietly showing the pre-edit messages.
      state.seen = []
      state.registry = []
      events.push({
        kind: 'raw',
        traceId: turn.traceId,
        label: 'history edited',
        text:
          'The client re-sent a rewritten history this turn; the thread ' +
          'below restarts from its edited version.',
      })
    }
    const { fresh, drifted, reset } = splitRequest(request, state.seen)
    if (reset) {
      // The abandoned history's unanswered calls must not stay live: a
      // re-emitted call can share their ids, and a result would then
      // mutate the STALE event while the visible one showed no output.
      state.registry = []
    }
    if (drifted > 0) {
      // Announced, not absorbed: the client edited history it had already
      // sent. The transcript keeps showing the first-rendered version, so
      // the reader needs to know the wire diverged from it.
      events.push({
        kind: 'raw',
        traceId: turn.traceId,
        label: 'history edited',
        text:
          `${drifted.toString()} earlier message${drifted === 1 ? '' : 's'} ` +
          'changed since the previous turn; the transcript shows the ' +
          'original version. Open the full trace for the re-sent bytes.',
      })
    }
    newMessageCount += fresh.length
    for (const message of fresh) {
      emitMessage(events, message, turn.traceId, state.registry)
    }
    state.seen = [...request.messages]
  }

  const response = parseResponseMessage(turn.responseBody)
  if (response === null) {
    events.push({
      kind: 'raw',
      traceId: turn.traceId,
      label: 'response (unrecognized shape)',
      text: turn.responseBody ?? '(no body captured)',
    })
  } else {
    emitMessage(events, response, turn.traceId, state.registry)
    newMessageCount += 1
    // The next request's history will include this assistant message.
    state.seen.push(response)
  }

  return {
    traceId: turn.traceId,
    index,
    events,
    messageCount,
    newMessageCount,
    toolCount: request?.toolCount ?? 0,
    latencyMs: turn.latencyMs,
    promptTokens: turn.promptTokens,
    completionTokens: turn.completionTokens,
    model: turn.model,
    statusCode: turn.statusCode,
    timestamp: turn.timestamp,
  }
}

/**
 * Assemble the transcript of `turns`, oldest first.
 *
 * `omittedTurns` counts the turns of the run that come before `turns[0]`
 * and are not rendered (the viewer caps long runs). Numbering continues
 * past them, and when the first rendered request is a delta tail the merge
 * starts at the tail's absolute index: those earlier messages are withheld
 * by the cap, so announcing them as a gap would be false. The caller's
 * banner says what is withheld.
 */
export function buildTurns(turns: TrajectoryTurn[], omittedTurns = 0): TranscriptTurn[] {
  const state: MergeState = { registry: [], seen: [] }
  const first = turns[0]
  if (omittedTurns > 0 && first !== undefined && first.requestDelta !== null) {
    state.seen = missingMessages(first.requestDelta.fromIndex)
  }
  return turns.map((turn, i) => buildTurnSection(turn, omittedTurns + i + 1, state))
}

/** Every distinct tool name called in the trajectory, for the filter list. */
export function toolNames(events: TranscriptEvent[]): string[] {
  const names = new Set<string>()
  for (const event of events) {
    if (event.kind === 'tool') names.add(event.name)
  }
  return [...names].sort()
}

/** All searchable text an event carries. */
function searchableText(event: TranscriptEvent): string {
  if (event.kind === 'tool') {
    return `${event.name} ${event.args} ${event.result ?? ''}`
  }
  if (event.kind === 'raw') return `${event.label} ${event.text}`
  return event.text
}

export interface TranscriptFilter {
  /** Free text, case-insensitive substring. Empty means "no text filter". */
  text: string
  /** Exact tool name, or null for "any tool / no tool filter". */
  tool: string | null
}

function eventMatches(event: TranscriptEvent, filter: TranscriptFilter): boolean {
  if (filter.tool !== null && !(event.kind === 'tool' && event.name === filter.tool)) {
    return false
  }
  if (filter.text === '') return true
  return searchableText(event).toLowerCase().includes(filter.text.toLowerCase())
}

/**
 * Events matching the filter, in transcript order.
 *
 * Because each message appears in exactly one turn's delta, a
 * conversation-wide search never double-counts the re-sent prefix.
 */
export function filterEvents(
  events: TranscriptEvent[],
  filter: TranscriptFilter,
): TranscriptEvent[] {
  if (filter.text === '' && filter.tool === null) return events
  return events.filter((event) => eventMatches(event, filter))
}

/**
 * Index of the event the amber flag pin anchors to: the flagged trace's
 * first NON-SYSTEM event, or null when the flagged trace is outside the
 * rendered trajectory.
 *
 * System rows are skipped deliberately — a pin on the system prompt reads
 * as "the prompt is flagged", and dreamer flags are about agent behavior
 * (the judge is instructed to call a prompt problem out in its reason,
 * not to anchor on the prompt). Falls back to the trace's first event
 * only when it produced nothing else.
 */
export function flagAnchorIndex(
  events: TranscriptEvent[],
  flaggedTraceId: string,
): number | null {
  // The dreamer flags MODEL behavior, so the pin targets the model's action
  // in the flagged turn: the LAST model-side event of that trace — its
  // response (or the tool activity leading into it), not the request's
  // opening message. Pinning the first event put the flag on user (or, after
  // a history reset, the conversation's opening) messages, which reads as
  // blaming the user for the model's mistake.
  let anchor = -1
  for (const [i, e] of events.entries()) {
    if (e.traceId !== flaggedTraceId) continue
    if (e.kind === 'system' || e.kind === 'user') continue
    anchor = i
  }
  if (anchor !== -1) return anchor
  const any = events.findIndex((e) => e.traceId === flaggedTraceId)
  return any === -1 ? null : any
}

// ---------------------------------------------------------------------------
// Tool-payload display formatting
// ---------------------------------------------------------------------------

export type JsonSegmentKind = 'key' | 'string' | 'number' | 'literal' | 'plain'

export interface JsonSegment {
  kind: JsonSegmentKind
  text: string
}

/** Sticky lexer: string | number | literal | punctuation | whitespace. */
const JSON_LEX = /("(?:\\.|[^"\\])*")|(-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)|(true|false|null)|([{}[\],:])|[ \t\n\r]+/y

/**
 * Lex + reindent a JSON payload for display WITHOUT re-encoding it.
 * A JSON.parse/stringify round trip would corrupt numbers beyond double
 * precision (64-bit IDs, ns timestamps) and collapse duplicate keys, so
 * every value token is emitted verbatim from the source text; only the
 * whitespace between tokens is ours. Returns null when the text isn't
 * JSON (plain-text tool output) — the caller renders it as-is.
 */
export function jsonDisplaySegments(text: string): JsonSegment[] | null {
  try {
    JSON.parse(text)
  } catch {
    return null
  }
  const tokens = lexJson(text)
  // Unreachable after a successful parse, but if the lexer ever falls out
  // of sync we show the payload verbatim rather than a mangled coloring.
  return tokens === null ? null : layoutJsonTokens(tokens)
}

interface JsonTok {
  kind: 'string' | 'number' | 'literal' | 'punct'
  text: string
}

/**
 * The top-level entries of a JSON object, each value as its source tokens
 * joined without whitespace. Like `jsonDisplaySegments`, nothing is
 * re-encoded, so a 64-bit `seed` reads back exactly as it was sent.
 * Returns null when the text is not a JSON object.
 */
export function jsonObjectEntries(text: string): Map<string, string> | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return null
  }
  if (!isRecord(parsed)) return null
  const tokens = lexJson(text)
  if (tokens === null) return null
  // The parse above proved the shape: `{` (key `:` value (`,` key `:` value)*)? `}`.
  const entries = new Map<string, string>()
  let i = 1
  while (i < tokens.length) {
    const key = tokens[i]
    if (key === undefined || key.kind !== 'string') break
    const name: unknown = JSON.parse(key.text)
    if (typeof name !== 'string') break
    const start = i + 2
    const end = jsonValueEnd(tokens, start)
    entries.set(name, tokens.slice(start, end).map((tok) => tok.text).join(''))
    i = tokens[end]?.text === ',' ? end + 1 : end
  }
  return entries
}

/** Index just past the value that starts at `start`; nesting is matched by depth. */
function jsonValueEnd(tokens: JsonTok[], start: number): number {
  let depth = 0
  for (let i = start; i < tokens.length; i += 1) {
    const tok = tokens[i]
    if (tok?.kind === 'punct' && (tok.text === '{' || tok.text === '[')) depth += 1
    else if (tok?.kind === 'punct' && (tok.text === '}' || tok.text === ']')) depth -= 1
    if (depth === 0) return i + 1
  }
  return tokens.length
}

function lexJson(text: string): JsonTok[] | null {
  const tokens: JsonTok[] = []
  let pos = 0
  while (pos < text.length) {
    JSON_LEX.lastIndex = pos
    const m = JSON_LEX.exec(text)
    if (m === null) return null
    if (m[1] !== undefined) tokens.push({ kind: 'string', text: m[1] })
    else if (m[2] !== undefined) tokens.push({ kind: 'number', text: m[2] })
    else if (m[3] !== undefined) tokens.push({ kind: 'literal', text: m[3] })
    else if (m[4] !== undefined) tokens.push({ kind: 'punct', text: m[4] })
    pos = JSON_LEX.lastIndex
  }
  return tokens
}

/** Second pass: classify value tokens and synthesize indentation. */
function layoutJsonTokens(tokens: JsonTok[]): JsonSegment[] {
  const out: JsonSegment[] = []
  const plain = (t: string): void => {
    out.push({ kind: 'plain', text: t })
  }
  const indent = (depth: number): string => `\n${'  '.repeat(depth)}`
  let depth = 0
  tokens.forEach((tok, i) => {
    const prev = tokens[i - 1]
    const next = tokens[i + 1]
    if (tok.kind !== 'punct') {
      const isKey = tok.kind === 'string' && next?.kind === 'punct' && next.text === ':'
      out.push({ kind: isKey ? 'key' : tok.kind, text: tok.text })
    } else if (tok.text === '{' || tok.text === '[') {
      plain(tok.text)
      depth += 1
      const emptyClose = next?.kind === 'punct' && (next.text === '}' || next.text === ']')
      if (!emptyClose) plain(indent(depth))
    } else if (tok.text === '}' || tok.text === ']') {
      depth -= 1
      const afterOpen = prev?.kind === 'punct' && (prev.text === '{' || prev.text === '[')
      if (!afterOpen) plain(indent(depth))
      plain(tok.text)
    } else if (tok.text === ',') {
      plain(',')
      plain(indent(depth))
    } else {
      plain(': ')
    }
  })
  return out
}
